import {
  isIOS,
  isPWA,
  getServiceWorkerRegistrations,
  unregisterServiceWorker,
  urlBase64ToUint8Array,
} from './util.js';
import { createPageControllers } from './page_controllers.js';
import i18next from 'i18next';
import enTranslation from '../static/locales/en/translation.json';
import zhTWTranslation from '../static/locales/zh-TW/translation.json';

export class DOMManager {
  constructor(apiClient, eventBus, serviceWorker) {
    this.apiClient = apiClient;
    this.eventBus = eventBus;
    this.serviceWorker = serviceWorker;

    this.eventBus.addEventListener('api:mobileUuidUpdated', (event) => {
      const { uuid } = event.detail;
      this.updateMobileUuid(uuid);
    });
    this.eventBus.addEventListener('api:accessTokenRenewed', (event) => {
      const { username } = event.detail;
      this.showLoggedInState(username);
    });
    this.eventBus.addEventListener('api:accessTokenRenewalFailed', () => {
      this.showLoginState();
    });

    this.navbar = document.getElementById('navbar');
    this.pages = {
      'login-page': document.getElementById('login-page'),
      'subscription-page': document.getElementById('subscription-page'),
      'notification-page': document.getElementById('notification-page'),
      'history-page': document.getElementById('history-page'),
      'overview-page': document.getElementById('overview-page'),
      'pwa-guide-page': document.getElementById('pwa-guide-page'),
      'settings-page': document.getElementById('settings-page'),
      'error-page': document.getElementById('error-page'),
    };

    this.previousPage = null;
    this.currentPage = null;

    // Navigation elements
    this.brand = document.getElementById('navbar-brand');
    this.offcanvas = document.getElementById('offcanvasNavbar');
    this.subscriptionNavLink = document.getElementById('subscription-nav-link');
    this.notificationNavLink = document.getElementById('notification-nav-link');
    this.historyNavLink = document.getElementById('history-nav-link');
    this.overviewNavLink = document.getElementById('overview-nav-link');
    this.settingsNavLink = document.getElementById('settings-nav-link');
    this.langEnNavItem = document.getElementById('lang-en-nav-item');
    this.langZhTwNavItem = document.getElementById('lang-zh-tw-nav-item');
    this.loginNavLink = document.getElementById('login-nav-link');
    this.logoutNavLink = document.getElementById('logout-nav-link');
    this.userNavItem = document.getElementById('user-nav-item');
    this.notLoggedInMenu = document.getElementById('not-logged-in-menu');
    this.loggedInMenu = document.getElementById('logged-in-menu');

    // Login Page elements
    this.usernameInput = document.getElementById('login-username');
    this.passwordInput = document.getElementById('login-password');
    this.loginButton = document.getElementById('login-submit');
    this.registerButton = document.getElementById('register-submit');

    // Subscription Page elements
    this.pwaGuideLink = document.getElementById('pwa-guide-link');
    this.notificationLink = document.getElementById('notification-link');
    this.newSubscriptionTokenInput = document.getElementById(
      'new-subscription-token'
    );
    this.newSubscriptionButton = document.getElementById(
      'new-subscription-button'
    );
    this.subscriptionListTbody = document.getElementById('subscription-list');

    // Notification Page elements
    this.notificationLoginButton = document.getElementById(
      'notification-login-button'
    );
    this.notificationPanel = document.getElementById('notification-panel');
    this.newNotificationNameInput = document.getElementById(
      'new-notification-name'
    );
    this.newNotificationNamePrefillInput = document.getElementById(
      'new-notification-name-prefill'
    );
    this.newNotificationDescriptionInput = document.getElementById(
      'new-notification-description'
    );
    this.newNotificationButton = document.getElementById(
      'new-notification-button'
    );
    this.newNotificationModalButton = document.getElementById(
      'new-notification-modal-button'
    );
    this.newNotificationModal = document.getElementById(
      'new-notification-modal'
    );
    this.notificationListTbody = document.getElementById('notification-list');

    // History Page elements
    this.historyList = document.getElementById('history-list');
    this.historySentinel = document.getElementById('history-sentinel');
    this.historySearchInput = document.getElementById('history-search-input');
    this.historySearchButton = document.getElementById('history-search-button');
    this.historyClearButton = document.getElementById('history-clear-button');

    // PWA Guide Page elements
    this.pwaGuidePrevButton = document.getElementById('pwa-guide-prev-button');
    this.pwaGuideTabs = {
      android: document.getElementById('android-pwa-tab'),
      ios: document.getElementById('ios-pwa-tab'),
    };
    this.pwaGuidePanels = {
      android: document.getElementById('android-pwa-panel'),
      ios: document.getElementById('ios-pwa-panel'),
    };

    // Overview Page elements
    this.apiOriginSpan = document.getElementById('api-origin');

    // Settings Page elements
    this.mobileUuidSpan = document.getElementById('mobile-uuid');
    this.notificationPermissionButton = document.getElementById(
      'notification-permission-button'
    );
    this.unregisterServiceWorkerButton = document.getElementById(
      'unregister-service-worker-button'
    );
    this.serviceWorkerStatusBadge = document.getElementById(
      'service-worker-status'
    );
    this.notificationPermissionStatusBadge = document.getElementById(
      'notification-permission-status'
    );

    // Error Page elements
    this.errorMessage = document.getElementById('error-message');
    this.errorStack = document.getElementById('error-stack');

    // Confirmation Modal elements
    this.confirmationModal = document.getElementById('confirmation-modal');
    this.confirmationModalBody = document.getElementById(
      'confirmation-modal-body'
    );
    this.confirmationModalConfirmButton = document.getElementById(
      'confirmation-modal-confirm-button'
    );

    // Info Modal elements
    this.infoModal = document.getElementById('info-modal');
    this.infoModalBody = document.getElementById('info-modal-body');
    this.infoModalTestButton = document.getElementById(
      'info-modal-test-button'
    );

    // Toast elements
    this.toastContainer = document.getElementById('toast-container');

    this.currentNotification = null;
    this.currentUser = null;

    this.historyLimit = 10;
    this.historyCursor = '';
    this.historyLoading = false;
    this.historyHasMore = true;
    this.historyObserver = null;
    this.historyTargetID = null;
    this.historyTargetFound = false;
    this.historyTargetMode = false;

    this.pageControllers = createPageControllers({
      'login-page': {
        onEnter: () => {
          this.clearInputValue(this.usernameInput);
          this.clearInputValue(this.passwordInput);
          this.setLoginInputsInvalid(false);
        },
      },
      'subscription-page': { onEnter: () => this.updateSubscriptions() },
      'notification-page': { onEnter: () => this.updateNotifications() },
      'history-page': {
        onEnter: () => this.showHistoryPage(),
        onLeave: () => this.disconnectHistoryObserver(),
      },
      'overview-page': {},
      'pwa-guide-page': {},
      'settings-page': {
        onEnter: () => {
          this.updateNotificationPermissionButtonState();
          this.updateServiceWorkerStatus();
          this.updateNotificationPermissionStatus();
        },
      },
      'error-page': { onEnter: () => this.hide(this.navbar) },
    });

    this.initI18next();

    this.initNavigationBar();
    this.initLoginPage();
    this.initSubscriptionPage();
    this.initNotificationPage();
    this.initHistoryPage();
    this.initPwaGuidePage();
    this.initOverviewPage();
    this.initSettingsPage();

    this.enableBootstrapComponents();
  }

  initPage() {
    const initialHash = this.getCurrentHash();
    const pageFromHash = this.getPageFromHash(initialHash);
    const initialPage = pageFromHash || this.pages['overview-page'];

    window.addEventListener('popstate', (event) => this.handlePopState(event));

    this.showPage(initialPage, false);
  }

  handlePopState(event) {
    const pageFromState = this.getPageFromState(event.state);
    if (pageFromState) {
      this.showPage(pageFromState, false);
      return;
    }

    // Fallback to hash
    const currentHash = this.getCurrentHash();
    const pageFromHash = this.getPageFromHash(currentHash);
    if (pageFromHash) {
      this.showPage(pageFromHash, false);
    }
  }

  getPageFromHash(hash) {
    if (!hash) {
      return null;
    }

    return (
      Object.values(this.pages).find((page) => page && page.id === hash) || null
    );
  }

  getCurrentHash() {
    const rawHash = window.location.hash || '';
    const withoutHash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
    // 移除 # 後面可能帶的參數，例如 login-page?foo=bar
    return withoutHash.split('?')[0];
  }

  getPageFromState(state) {
    if (!state || !state.page) {
      return null;
    }

    return this.pages[state.page] || null;
  }

  showPage(pageElement, pushState = true) {
    if (this.currentPage === pageElement) {
      return;
    }

    const currentPageController = this.currentPage
      ? this.pageControllers[this.currentPage.id]
      : null;
    currentPageController?.leave();

    Object.values(this.pages).forEach((page) => {
      page.style.display = 'none';
    });
    pageElement.style.display = 'block';

    const pageKey = Object.keys(this.pages).find(
      (key) => this.pages[key] === pageElement
    );
    const hash = pageElement.id || pageKey;
    if (
      pushState &&
      pageKey &&
      window.history &&
      window.history.pushState &&
      hash
    ) {
      window.history.pushState({ page: pageKey }, '', `#${hash}`);
    }

    this.pageControllers[pageKey]?.enter();

    // Scroll to top
    setTimeout(() => window.scrollTo(0, 0), 0);

    this.previousPage = this.currentPage;
    this.currentPage = pageElement;
  }

  showOffcanvas() {
    bootstrap.Offcanvas.getOrCreateInstance(this.offcanvas).show();
  }

  hideOffcanvas() {
    bootstrap.Offcanvas.getOrCreateInstance(this.offcanvas).hide();
  }

  removeURLParameters() {
    const url = new URL(window.location);
    url.search = '';
    window.history.replaceState(null, '', url);
  }

  initNavigationBar() {
    this.brand.addEventListener('click', () => {
      this.removeURLParameters();
      this.showPage(this.pages['overview-page']);
    });

    this.subscriptionNavLink.addEventListener('click', () => {
      this.removeURLParameters();
      this.showPage(this.pages['subscription-page']);
      this.hideOffcanvas();
    });

    this.notificationNavLink.addEventListener('click', () => {
      this.removeURLParameters();
      this.showPage(this.pages['notification-page']);
      this.hideOffcanvas();
    });

    this.historyNavLink.addEventListener('click', () => {
      this.removeURLParameters();
      this.showPage(this.pages['history-page']);
      this.hideOffcanvas();
    });

    this.overviewNavLink.addEventListener('click', () => {
      this.removeURLParameters();
      this.showPage(this.pages['overview-page']);
      this.hideOffcanvas();
    });

    this.settingsNavLink.addEventListener('click', () => {
      this.removeURLParameters();
      this.showPage(this.pages['settings-page']);
      this.hideOffcanvas();
    });

    this.langEnNavItem.addEventListener('click', () => {
      this.changeLanguage('en');
    });

    this.langZhTwNavItem.addEventListener('click', () => {
      this.changeLanguage('zh-TW');
    });

    this.loginNavLink.addEventListener('click', () => {
      this.removeURLParameters();
      this.showPage(this.pages['login-page']);
    });

    this.logoutNavLink.addEventListener('click', () => {
      this.apiClient.logout();
      this.hide(this.loggedInMenu);
      this.show(this.notLoggedInMenu);
      this.removeURLParameters();
      this.showPage(this.pages['login-page']);
    });
  }

  initLoginPage() {
    [this.usernameInput, this.passwordInput].forEach((input) => {
      input.addEventListener('input', () => this.setLoginInputsInvalid(false));
    });

    this.passwordInput.addEventListener('keyup', (event) => {
      if (event.key === 'Enter') {
        this.loginButton.click();
      }
    });

    this.loginButton.addEventListener('click', async () => {
      const username = this.usernameInput.value;
      const password = this.passwordInput.value;
      try {
        const res = await this.apiClient.login(username, password);
        if (res.ok) {
          this.showLoggedInState(username);

          this.showPage(this.previousPage || this.pages['subscription-page']);
        } else {
          let messageKey = 'error_occurred';

          try {
            const body = await res.json();
            if (body && body.code === 'AUTHORIZATION') {
              messageKey = 'authorization_failed';
            }
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }

          this.addToast(
            'danger',
            i18next.t('login_failed_title'),
            i18next.t('just_now'),
            i18next.t(messageKey)
          );
          this.setLoginInputsInvalid(true);
        }
      } catch (error) {
        console.error('Login request failed:', error);
      }
    });

    this.registerButton.addEventListener('click', async () => {
      const username = this.usernameInput.value;
      const password = this.passwordInput.value;
      try {
        const res = await this.apiClient.register(username, password);
        if (res.ok) {
          this.showLoggedInState(username);

          this.showPage(this.previousPage || this.pages['subscription-page']);
        } else {
          let messageKey = 'error_occurred';

          try {
            const body = await res.json();
            if (body && body.code === 'VALIDATION') {
              messageKey = 'register_validation_failed';
            } else if (body && body.code === 'CONFLICT') {
              messageKey = 'register_conflict';
            }
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }

          this.addToast(
            'danger',
            i18next.t('register_failed_title'),
            i18next.t('just_now'),
            i18next.t(messageKey)
          );
          this.setLoginInputsInvalid(true);
        }
      } catch (error) {
        console.error('Registration request failed:', error);
      }
    });
  }

  setLoginInputsInvalid(isInvalid) {
    [this.usernameInput, this.passwordInput].forEach((input) => {
      input.classList.toggle('is-invalid', isInvalid);
    });
  }

  initSubscriptionPage() {
    this.pwaGuideLink.addEventListener('click', () => {
      this.showPWAGuide();
    });

    this.notificationLink.addEventListener('click', () => {
      this.showPage(this.pages['notification-page']);
    });

    this.newSubscriptionButton.addEventListener('click', async () => {
      const token = this.newSubscriptionTokenInput.value.trim();
      this.clearInputValue(this.newSubscriptionTokenInput);

      // request notification permission if not granted
      if (Notification.permission === 'default') {
        try {
          const permission = await Notification.requestPermission();
          console.log('Notification permission:', permission);

          if (permission === 'granted') {
            // Get VAPID public key and store in localStorage
            const res = await this.apiClient.getVapidPublicKey();
            if (res.ok) {
              const vapidPublicKey = await res.text();

              // Get subscription
              const subscription = (
                await this.serviceWorker.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
                })
              ).toJSON();
              console.log('Push Subscription created');

              // Update mobile
              await this.apiClient.updateMobile(subscription);
            } else {
              console.error('Failed to get VAPID public key:', res.statusText);
            }
          } else {
            console.log('Notification permission not granted:', permission);
            this.addToast(
              'danger',
              i18next.t('subscribe_failed_title'),
              i18next.t('just_now'),
              i18next.t('permission_not_granted')
            );
            return;
          }
        } catch (err) {
          console.error('Error requesting notification permission:', err);
          this.addToast(
            'danger',
            i18next.t('subscribe_failed_title'),
            i18next.t('just_now'),
            i18next.t('error_occurred')
          );
          return;
        }
      } else if (Notification.permission === 'denied') {
        console.log('Notification permission denied');
        this.addToast(
          'danger',
          i18next.t('subscribe_failed_title'),
          i18next.t('just_now'),
          i18next.t('permission_not_granted')
        );
        return;
      }

      // create subscription
      try {
        const res = await this.apiClient.createSubscription(token);
        if (res.ok) {
          this.updateSubscriptions();
          this.addToast(
            'success',
            i18next.t('subscribe_success_title'),
            i18next.t('just_now'),
            i18next.t('subscribe_success_body')
          );
        } else {
          let messageKey = 'error_occurred';

          try {
            const body = await res.json();
            if (body && body.code === 'NOT_FOUND') {
              messageKey = 'token_not_found';
            } else if (body && body.code === 'CONFLICT') {
              messageKey = 'existing_subscription';
            }
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }

          this.addToast(
            'danger',
            i18next.t('subscribe_failed_title'),
            i18next.t('just_now'),
            i18next.t(messageKey)
          );
        }
      } catch (error) {
        console.error('Create subscription request failed:', error);
      }
    });
  }

  initNotificationPage() {
    this.currentEditingId = null;

    this.infoModalTestButton.addEventListener('click', async () => {
      if (!this.currentNotification) return;

      const { id, name, description, token } = this.currentNotification;
      const username = this.currentUser || 'Unknown';
      const body = `[${username}]: [${id}] [${name}] [${description}] [${token}]`;

      try {
        const res = await this.apiClient.testNotification(token, body);
        if (res.ok) {
          console.log('Test notification sent successfully');
          this.addToast(
            'success',
            i18next.t('test_notification_sent'),
            i18next.t('just_now'),
            i18next.t('test_notification_sent_body')
          );
        } else {
          console.error('Failed to send test notification:', res.statusText);
          this.addToast(
            'danger',
            i18next.t('test_notification_failed'),
            i18next.t('just_now'),
            i18next.t('test_notification_failed_body')
          );
        }
      } catch (error) {
        console.error('Test notification request failed:', error);
        this.addToast(
          'danger',
          i18next.t('test_notification_failed'),
          i18next.t('just_now'),
          i18next.t('test_notification_failed_body')
        );
      }
    });

    this.notificationLoginButton.addEventListener('click', () => {
      this.showPage(this.pages['login-page']);
    });

    this.newNotificationModalButton.addEventListener('click', async () => {
      this.currentEditingId = null;

      this.newNotificationButton.textContent = i18next.t('create');
      this.newNotificationButton.setAttribute('data-i18n', 'create');

      const prefillName = this.newNotificationNamePrefillInput
        ? this.newNotificationNamePrefillInput.value.trim()
        : '';
      this.newNotificationNameInput.value = prefillName;
      this.clearInputValue(this.newNotificationDescriptionInput);

      bootstrap.Modal.getOrCreateInstance(this.newNotificationModal).show();
    });

    this.newNotificationButton.addEventListener('click', async () => {
      const wasEditing = this.currentEditingId !== null;
      const name = this.newNotificationNameInput.value.trim();
      const description = this.newNotificationDescriptionInput.value.trim();
      try {
        let res;
        if (wasEditing) {
          // Update
          res = await this.apiClient.updateNotification(this.currentEditingId, {
            name,
            description,
          });
        } else {
          // Create
          res = await this.apiClient.createNotification(name, description);
        }

        if (res.ok) {
          const createdNotification = wasEditing ? null : await res.json();
          await this.updateNotifications();

          this.clearInputValue(this.newNotificationNameInput);
          this.clearInputValue(this.newNotificationDescriptionInput);
          if (!wasEditing) {
            this.clearInputValue(this.newNotificationNamePrefillInput);
          }
          this.currentEditingId = null;

          if (createdNotification) {
            this.newNotificationModal.addEventListener(
              'hidden.bs.modal',
              () => this.showNotificationInfo(createdNotification),
              { once: true }
            );
          }
          bootstrap.Modal.getOrCreateInstance(this.newNotificationModal).hide();
        } else {
          console.error(
            'Failed to create/update notification:',
            res.statusText
          );
        }
      } catch (error) {
        console.error('Create/update notification request failed:', error);
      }
    });
  }

  initHistoryPage() {
    this.disconnectHistoryObserver();

    if (this.historySearchButton) {
      this.historySearchButton.addEventListener('click', async () => {
        await this.runHistorySearch();
      });
    }

    if (this.historyClearButton) {
      this.historyClearButton.addEventListener('click', () => {
        this.showConfirmationModal(
          i18next.t('history_clear_confirmation'),
          async () => {
            localStorage.setItem('historyClearedAt', new Date().toISOString());
            this.resetHistoryState();
            await this.loadHistoryPage();
          }
        );
      });
    }

    if (this.historySearchInput) {
      this.historySearchInput.addEventListener('keyup', async (event) => {
        if (event.key === 'Enter') {
          await this.runHistorySearch();
        }
      });
    }
  }

  async showHistoryPage() {
    const idFromURL = this.getHistoryTargetIDFromURL();
    if (this.historySearchInput) {
      this.historySearchInput.value = idFromURL;
    }

    await this.runHistorySearch();
  }

  async runHistorySearch() {
    const targetID = this.historySearchInput
      ? this.historySearchInput.value.trim()
      : '';
    this.historyTargetID = targetID;
    this.historyTargetMode = Boolean(targetID);
    this.syncHistoryQuery(targetID);

    this.resetHistoryState();

    await this.loadHistoryPage();

    if (!this.historyTargetMode && this.historyHasMore) {
      this.setupHistoryObserver();
    }
  }

  syncHistoryQuery(id) {
    const page = this.pages['history-page'];
    const hash = page && page.id ? page.id : 'history-page';
    const url = new URL(window.location.href);

    if (id) {
      url.searchParams.set('id', id);
    } else {
      url.search = '';
    }

    const finalURL = `${url.pathname}${url.search}#${hash}`;
    window.history.replaceState({ page: 'history-page' }, '', finalURL);
  }

  resetHistoryState() {
    this.disconnectHistoryObserver();
    this.historyCursor = '';
    this.historyLoading = false;
    this.historyHasMore = true;
    this.historyTargetFound = false;
    this.clearElementHTML(this.historyList);
  }

  setupHistoryObserver() {
    this.disconnectHistoryObserver();
    if (this.historyTargetMode) {
      return;
    }

    if (!this.historySentinel) {
      return;
    }

    let firstCallback = true;
    this.historyObserver = new IntersectionObserver(
      async (entries) => {
        if (firstCallback) {
          firstCallback = false;
          return;
        }
        const entry = entries[0];
        if (
          entry &&
          entry.isIntersecting &&
          !this.historyLoading &&
          this.historyHasMore &&
          this.currentPage === this.pages['history-page']
        ) {
          await this.loadHistoryPage();
        }
      },
      { root: null, threshold: 0.2 }
    );

    this.historyObserver.observe(this.historySentinel);
  }

  disconnectHistoryObserver() {
    if (this.historyObserver) {
      this.historyObserver.disconnect();
      this.historyObserver = null;
    }
  }

  getHistoryTargetIDFromURL() {
    const id = new URLSearchParams(window.location.search).get('id');
    return id ? id.trim() : '';
  }

  async loadHistoryPage() {
    if (this.historyLoading || !this.historyHasMore) {
      return;
    }

    this.historyLoading = true;

    try {
      let res;
      if (this.historyTargetMode) {
        res = await this.apiClient.getMobileNotificationHistory(
          this.historyTargetID
        );
      } else {
        res = await this.apiClient.getMobileNotificationHistories(
          this.historyLimit,
          this.historyCursor
        );
      }

      if (!res.ok) {
        this.addToast(
          'danger',
          i18next.t('history_load_failed_title'),
          i18next.t('just_now'),
          i18next.t('history_load_failed')
        );
        return;
      }

      const data = await res.json();
      const items = this.normalizeHistoryItems(data);

      const clearedAt = this.historyTargetMode
        ? null
        : localStorage.getItem('historyClearedAt');

      let hitClearBoundary = false;
      items.forEach((item) => {
        if (hitClearBoundary) return;
        if (clearedAt && new Date(item.requested_at) <= new Date(clearedAt)) {
          hitClearBoundary = true;
          return;
        }
        if (!this.historyTargetMode || this.isHistoryTargetItem(item)) {
          this.historyList.appendChild(this.createHistoryCard(item));
        }
        this.tryHighlightTarget(item);
      });

      if (this.historyTargetMode) {
        this.historyCursor = '';
        this.historyHasMore = false;
      } else if (hitClearBoundary) {
        this.historyCursor = '';
        this.historyHasMore = false;
      } else {
        this.historyCursor = data.next_cursor || '';
        this.historyHasMore = Boolean(data.next_cursor);
      }

      if (this.historyList.children.length === 0) {
        this.addToast(
          'info',
          i18next.t('history_empty_title'),
          i18next.t('just_now'),
          i18next.t('history_empty')
        );
      }
    } catch (error) {
      console.error('Fetch history request failed:', error);
      this.addToast(
        'danger',
        i18next.t('history_load_failed_title'),
        i18next.t('just_now'),
        i18next.t('history_load_failed')
      );
    } finally {
      this.historyLoading = false;
    }
  }

  normalizeHistoryItems(data) {
    if (this.historyTargetMode) {
      if (!data || Array.isArray(data)) {
        return [];
      }

      return [data];
    }

    return Array.isArray(data.items) ? data.items : [];
  }

  tryHighlightTarget(item) {
    if (!this.historyTargetID || this.historyTargetFound) {
      return;
    }

    if (!this.isHistoryTargetItem(item)) {
      return;
    }

    const card = document.getElementById(
      `history-notification-log-${item.notification_log_id}`
    );
    if (card) {
      card.classList.add('history-card--target');
      this.historyTargetFound = true;
      this.historyHasMore = false;
    }
  }

  isHistoryTargetItem(item) {
    if (!this.historyTargetID) {
      return false;
    }

    const target = this.historyTargetID;
    return (
      String(item.notification_log_id) === target ||
      String(item.delivery_log_id) === target
    );
  }

  createHistoryCard(item) {
    const card = document.createElement('article');
    card.classList.add('history-card');
    card.id = `history-notification-log-${item.notification_log_id}`;

    const deliveryAnchor = document.createElement('span');
    deliveryAnchor.classList.add('d-none');
    deliveryAnchor.id = `history-delivery-log-${item.delivery_log_id}`;
    card.appendChild(deliveryAnchor);

    const title = document.createElement('h3');
    title.classList.add('history-card__title');
    title.textContent = item.title || item.notification_name || '-';

    const body = document.createElement('p');
    body.classList.add('history-card__body');
    body.textContent = item.body || item.notification_description || '';

    const meta = document.createElement('div');
    meta.classList.add('history-card__meta');

    const status = document.createElement('span');
    status.classList.add('history-card__status', `is-${item.delivery_status}`);
    const statusLabel = document.createElement('span');
    statusLabel.setAttribute('data-i18n', 'history_delivery_status');
    statusLabel.textContent = i18next.t('history_delivery_status');
    const statusValue = document.createElement('span');
    statusValue.textContent = `: ${item.delivery_status}`;
    status.appendChild(statusLabel);
    status.appendChild(statusValue);

    const requested = document.createElement('span');
    const requestedLabel = document.createElement('span');
    requestedLabel.setAttribute('data-i18n', 'history_requested_at');
    requestedLabel.textContent = i18next.t('history_requested_at');
    const requestedValue = document.createElement('span');
    requestedValue.textContent = `: ${new Date(item.requested_at).toLocaleString()}`;
    requested.appendChild(requestedLabel);
    requested.appendChild(requestedValue);

    meta.appendChild(status);
    meta.appendChild(requested);

    if (item.delivery_delivered_at) {
      const delivered = document.createElement('span');
      delivered.classList.add('history-card__meta-extra');
      const deliveredLabel = document.createElement('span');
      deliveredLabel.setAttribute('data-i18n', 'history_delivered_at');
      deliveredLabel.textContent = i18next.t('history_delivered_at');
      const deliveredValue = document.createElement('span');
      deliveredValue.textContent = `: ${new Date(item.delivery_delivered_at).toLocaleString()}`;
      delivered.appendChild(deliveredLabel);
      delivered.appendChild(deliveredValue);
      meta.appendChild(delivered);
    }

    const ids = document.createElement('div');
    ids.classList.add('history-card__ids');
    const notificationLogLabel = document.createElement('span');
    notificationLogLabel.setAttribute(
      'data-i18n',
      'history_notification_log_id'
    );
    notificationLogLabel.textContent = i18next.t('history_notification_log_id');
    const notificationLogValue = document.createElement('span');
    notificationLogValue.textContent = `: ${item.notification_log_id}`;
    const separator = document.createTextNode(' | ');
    const deliveryLogLabel = document.createElement('span');
    deliveryLogLabel.setAttribute('data-i18n', 'history_delivery_log_id');
    deliveryLogLabel.textContent = i18next.t('history_delivery_log_id');
    const deliveryLogValue = document.createElement('span');
    deliveryLogValue.textContent = `: ${item.delivery_log_id}`;

    ids.appendChild(notificationLogLabel);
    ids.appendChild(notificationLogValue);
    ids.appendChild(separator);
    ids.appendChild(deliveryLogLabel);
    ids.appendChild(deliveryLogValue);

    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(meta);
    card.appendChild(ids);

    return card;
  }

  initPwaGuidePage() {
    this.pwaGuidePrevButton.addEventListener('click', () => {
      this.showPage(this.previousPage || this.pages['subscription-page']);
    });
    Object.entries(this.pwaGuideTabs).forEach(([platform, tab]) => {
      tab.addEventListener('click', () =>
        this.selectPwaGuidePlatform(platform)
      );
    });
    this.selectPwaGuidePlatform(isIOS() ? 'ios' : 'android');
  }

  selectPwaGuidePlatform(platform) {
    Object.entries(this.pwaGuideTabs).forEach(([name, tab]) => {
      const isSelected = name === platform;
      tab.classList.toggle('active', isSelected);
      tab.setAttribute('aria-selected', String(isSelected));
      this.pwaGuidePanels[name].hidden = !isSelected;
    });
  }

  initOverviewPage() {
    this.apiOriginSpan.textContent = window.location.origin;
  }

  initSettingsPage() {
    this.updateMobileUuid(this.apiClient.mobileUuid);
    this.notificationPermissionButton.addEventListener('click', async () => {
      if (!('Notification' in window)) {
        this.addToast(
          'danger',
          i18next.t('notification_not_supported'),
          i18next.t('just_now'),
          i18next.t('notification_not_supported_body')
        );
        return;
      }

      if (Notification.permission === 'granted') {
        this.updateNotificationPermissionButtonState();
        this.updateNotificationPermissionStatus();
        return;
      }

      if (Notification.permission === 'default') {
        try {
          const permission = await Notification.requestPermission();
          console.log('Notification permission:', permission);

          if (permission === 'granted') {
            this.addToast(
              'success',
              i18next.t('notification_permission_granted'),
              i18next.t('just_now'),
              i18next.t('notification_permission_granted_body')
            );
          } else if (permission === 'denied') {
            this.addToast(
              'danger',
              i18next.t('notification_permission_denied'),
              i18next.t('just_now'),
              i18next.t('notification_permission_denied_body')
            );
          }

          this.updateNotificationPermissionButtonState();
          this.updateNotificationPermissionStatus();
          return;
        } catch (error) {
          console.error('Failed to request notification permission:', error);
          this.addToast(
            'danger',
            i18next.t('error_occurred'),
            i18next.t('just_now'),
            i18next.t('errror_occurred_body')
          );
          return;
        }
      }

      if (Notification.permission === 'denied') {
        this.addToast(
          'danger',
          i18next.t('notification_permission_denied'),
          i18next.t('just_now'),
          i18next.t('notification_permission_denied_body')
        );
      }
    });

    this.unregisterServiceWorkerButton.addEventListener('click', async () => {
      try {
        await unregisterServiceWorker();
        this.updateServiceWorkerStatus();
        this.addToast(
          'success',
          i18next.t('service_worker_unregistered'),
          i18next.t('just_now'),
          i18next.t('service_worker_unregistered_body')
        );
      } catch (error) {
        console.error('Failed to unregister service worker:', error);
        this.addToast(
          'danger',
          i18next.t('failed_to_unregister_service_worker'),
          i18next.t('just_now'),
          i18next.t('failed_to_unregister_service_worker_body')
        );
      }
    });
  }

  updateNotificationPermissionButtonState() {
    if (!this.notificationPermissionButton) {
      return;
    }

    if (!('Notification' in window)) {
      this.notificationPermissionButton.disabled = true;
      return;
    }

    this.notificationPermissionButton.disabled =
      Notification.permission === 'granted';
  }

  async updateServiceWorkerStatus() {
    if (!this.serviceWorkerStatusBadge) {
      return;
    }

    let isActive = false;
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await getServiceWorkerRegistrations();
        isActive = registrations && registrations.length > 0;
      } catch (error) {
        console.error('Failed to check service worker status:', error);
      }
    }

    const key = isActive
      ? 'service_worker_status_active'
      : 'service_worker_status_inactive';
    this.serviceWorkerStatusBadge.textContent = i18next.t(key);
    this.serviceWorkerStatusBadge.setAttribute('data-i18n', key);
    this.serviceWorkerStatusBadge.classList.remove(
      'text-bg-success',
      'text-bg-secondary'
    );
    this.serviceWorkerStatusBadge.classList.add(
      isActive ? 'text-bg-success' : 'text-bg-secondary'
    );
  }

  updateNotificationPermissionStatus() {
    if (!this.notificationPermissionStatusBadge) {
      return;
    }

    let key;
    let badgeClass;

    if (!('Notification' in window)) {
      key = 'notification_permission_status_unsupported';
      badgeClass = 'text-bg-secondary';
    } else {
      switch (Notification.permission) {
        case 'granted':
          key = 'notification_permission_status_granted';
          badgeClass = 'text-bg-success';
          break;
        case 'denied':
          key = 'notification_permission_status_denied';
          badgeClass = 'text-bg-danger';
          break;
        default:
          key = 'notification_permission_status_default';
          badgeClass = 'text-bg-warning';
          break;
      }
    }

    this.notificationPermissionStatusBadge.textContent = i18next.t(key);
    this.notificationPermissionStatusBadge.setAttribute('data-i18n', key);
    this.notificationPermissionStatusBadge.classList.remove(
      'text-bg-success',
      'text-bg-danger',
      'text-bg-warning',
      'text-bg-secondary'
    );
    this.notificationPermissionStatusBadge.classList.add(badgeClass);
  }

  showConfirmationModal(message, onConfirm) {
    this.confirmationModalBody.textContent = message;

    // Remove previous event listeners to avoid multiple calls
    const newButton = this.confirmationModalConfirmButton.cloneNode(true);
    this.confirmationModalConfirmButton.parentNode.replaceChild(
      newButton,
      this.confirmationModalConfirmButton
    );
    this.confirmationModalConfirmButton = newButton;

    this.confirmationModalConfirmButton.addEventListener('click', () => {
      onConfirm();
      bootstrap.Modal.getOrCreateInstance(this.confirmationModal).hide();
    });

    bootstrap.Modal.getOrCreateInstance(this.confirmationModal).show();
  }

  showPWAGuide() {
    if (isIOS() && !isPWA()) {
      this.hide(this.navbar);
      this.hide(this.pwaGuidePrevButton);
    }
    this.showPage(this.pages['pwa-guide-page']);
  }

  // Utility methods
  show(element, display = 'block') {
    if (element) element.style.display = display;
  }

  hide(element) {
    if (element) element.style.display = 'none';
  }

  clearElementHTML(element) {
    if (element) element.innerHTML = '';
  }

  clearInputValue(input) {
    if (input) input.value = '';
  }

  async updateNotifications() {
    try {
      const res = await this.apiClient.getNotifications();
      if (res.ok) {
        const notifications = await res.json();
        this.clearElementHTML(this.notificationListTbody);
        notifications.forEach((notification) => {
          this.createNotificationRow(notification);
        });
      } else {
        console.error('Failed to fetch notifications:', res.statusText);
      }
    } catch (error) {
      console.error('Fetch notifications request failed:', error);
    }
  }

  createNotificationRow(notification) {
    const card = document.createElement('div');
    card.classList.add('subscription-card');

    const cardBody = document.createElement('div');
    cardBody.classList.add('subscription-card__body');

    const headerRow = document.createElement('div');
    headerRow.classList.add('subscription-card__header');

    const nameText = document.createElement('div');
    nameText.classList.add('subscription-card__title');
    nameText.textContent = notification.name;

    headerRow.appendChild(nameText);

    const descText = document.createElement('div');
    descText.classList.add('subscription-card__desc');
    descText.textContent = notification.description || '';

    const infoList = document.createElement('div');
    infoList.classList.add('subscription-card__info');

    const tokenRow = document.createElement('div');
    tokenRow.classList.add('subscription-card__row');

    const tokenLabel = document.createElement('div');
    tokenLabel.classList.add('subscription-card__label');
    tokenLabel.setAttribute('data-i18n', 'token');
    tokenLabel.textContent = i18next.t('token');

    const tokenValue = document.createElement('div');
    tokenValue.classList.add('subscription-card__value');
    tokenValue.textContent = notification.token;
    tokenValue.style.cursor = 'pointer';
    tokenValue.addEventListener('click', () => {
      this.copyNotificationValue(notification.token);
    });

    tokenRow.appendChild(tokenLabel);
    tokenRow.appendChild(tokenValue);

    const createdRow = document.createElement('div');
    createdRow.classList.add('subscription-card__row');

    const createdLabel = document.createElement('span');
    createdLabel.classList.add('subscription-card__label');
    createdLabel.setAttribute('data-i18n', 'create_time');
    createdLabel.textContent = i18next.t('create_time');

    const createdValue = document.createElement('span');
    createdValue.classList.add('subscription-card__value');
    createdValue.textContent = new Date(
      notification.created_at
    ).toLocaleString();

    createdRow.appendChild(createdLabel);
    createdRow.appendChild(createdValue);

    infoList.appendChild(tokenRow);
    infoList.appendChild(createdRow);

    const actionRow = document.createElement('div');
    actionRow.classList.add('subscription-card__actions');

    const infoButton = document.createElement('button');
    infoButton.classList.add('btn', 'btn-info', 'btn-sm');
    infoButton.setAttribute('data-i18n', 'info');
    infoButton.textContent = i18next.t('info');
    infoButton.addEventListener('click', () =>
      this.showNotificationInfo(notification)
    );

    const updateButton = document.createElement('button');
    updateButton.classList.add('btn', 'btn-secondary', 'btn-sm');
    updateButton.setAttribute('data-i18n', 'update');
    updateButton.textContent = i18next.t('update');
    updateButton.addEventListener('click', () => {
      this.currentEditingId = notification.id;
      this.newNotificationNameInput.value = notification.name;
      this.newNotificationDescriptionInput.value =
        notification.description || '';

      this.newNotificationButton.textContent = i18next.t('update');
      this.newNotificationButton.setAttribute('data-i18n', 'update');

      bootstrap.Modal.getOrCreateInstance(this.newNotificationModal).show();
    });

    const logsCollapseId = `notification-logs-${notification.id}`;
    const logsButton = document.createElement('button');
    logsButton.classList.add('btn', 'btn-secondary', 'btn-sm');
    logsButton.setAttribute('data-i18n', 'logs');
    logsButton.textContent = i18next.t('logs');
    logsButton.setAttribute('data-bs-toggle', 'collapse');
    logsButton.setAttribute('data-bs-target', `#${logsCollapseId}`);
    logsButton.setAttribute('aria-expanded', 'false');
    logsButton.setAttribute('aria-controls', logsCollapseId);

    const deleteButton = document.createElement('button');
    deleteButton.classList.add('btn', 'btn-danger', 'btn-sm');
    deleteButton.setAttribute('data-i18n', 'delete');
    deleteButton.textContent = i18next.t('delete');
    deleteButton.addEventListener('click', () =>
      this.deleteNotification(notification.id)
    );

    actionRow.appendChild(infoButton);
    actionRow.appendChild(updateButton);
    actionRow.appendChild(logsButton);
    actionRow.appendChild(deleteButton);

    cardBody.appendChild(headerRow);
    if (descText.textContent) {
      cardBody.appendChild(descText);
    }
    cardBody.appendChild(infoList);
    cardBody.appendChild(actionRow);

    card.appendChild(cardBody);

    const logsCollapse = document.createElement('div');
    logsCollapse.classList.add('collapse', 'm-3');
    logsCollapse.id = logsCollapseId;

    const logsCardBody = document.createElement('div');
    logsCardBody.classList.add('card-body');
    logsCardBody.style.border = 'none';

    const logsContent = document.createElement('div');
    logsContent.setAttribute('data-i18n', 'loading');
    logsContent.textContent = i18next.t('loading');
    const logsHeader = document.createElement('div');
    logsHeader.classList.add('d-flex', 'justify-content-end', 'mb-2');

    const clearLogsButton = document.createElement('button');
    clearLogsButton.classList.add('btn', 'btn-outline-secondary', 'btn-sm');
    clearLogsButton.title = i18next.t('clear_logs');
    clearLogsButton.innerHTML = '<i class="bi bi-trash"></i>';
    clearLogsButton.addEventListener('click', () => {
      this.showConfirmationModal(i18next.t('clear_logs_confirmation'), () => {
        localStorage.setItem(
          'notification-logs-cleared-' + notification.id,
          new Date().toISOString()
        );
        logsContent.innerHTML = '';
        logsContent.setAttribute('data-i18n', 'no_logs');
        logsContent.textContent = i18next.t('no_logs');
      });
    });

    logsHeader.appendChild(clearLogsButton);
    logsCardBody.appendChild(logsHeader);
    logsCardBody.appendChild(logsContent);
    logsCollapse.appendChild(logsCardBody);

    logsCollapse.addEventListener('show.bs.collapse', async () => {
      try {
        const res = await this.apiClient.getNotificationLogs(notification.id);
        if (res.ok) {
          const logs = await res.json();

          const clearedAt = localStorage.getItem(
            'notification-logs-cleared-' + notification.id
          );
          const filteredLogs = clearedAt
            ? logs.filter(
                (log) =>
                  log.requested_at &&
                  new Date(log.requested_at) > new Date(clearedAt)
              )
            : logs;

          if (!Array.isArray(filteredLogs) || filteredLogs.length === 0) {
            logsContent.setAttribute('data-i18n', 'no_logs');
            logsContent.textContent = i18next.t('no_logs');
            return;
          }

          logsContent.removeAttribute('data-i18n');

          const lines = filteredLogs.map((log) => {
            // Define color variables
            const COLOR_TIME = '#888';
            const COLOR_STATUS_DEFAULT = '#888';
            const COLOR_STATUS_SUCCESS = '#28a745';
            const COLOR_STATUS_PARTIAL = '#ffc107';
            const COLOR_STATUS_FAILED = '#dc3545';
            const COLOR_IP = '#007bff';
            const COLOR_UA = '#6f42c1';

            const requestedAt = log.requested_at
              ? `<span style="color:${COLOR_TIME};">${new Date(log.requested_at).toLocaleString()}</span>`
              : '';
            let statusColor = COLOR_STATUS_DEFAULT;
            if (log.status === 'success') statusColor = COLOR_STATUS_SUCCESS;
            else if (log.status === 'partial')
              statusColor = COLOR_STATUS_PARTIAL;
            else if (log.status === 'failed') statusColor = COLOR_STATUS_FAILED;
            const status = `<span style="color:${statusColor};font-weight:bold;">${log.status}</span>`;
            const ip = `<span style="color:${COLOR_IP};">${log.ip}</span>`;
            const userAgent = `<span style="color:${COLOR_UA};">${log.user_agent}</span>`;
            return `${requestedAt} ${status} ${ip} ${userAgent}`.trim();
          });

          const pre = document.createElement('pre');
          pre.classList.add('mb-0', 'text-break');
          pre.style.maxHeight = '200px';
          pre.style.overflowY = 'auto';
          pre.style.whiteSpace = 'pre-wrap';
          pre.style.overflowX = 'hidden';
          pre.innerHTML = lines.join('<br>');

          logsContent.innerHTML = '';
          logsContent.appendChild(pre);
        } else {
          logsContent.setAttribute('data-i18n', 'failed_to_load_logs');
          logsContent.textContent = i18next.t('failed_to_load_logs');
          console.error('Failed to fetch notification logs:', res.statusText);
        }
      } catch (error) {
        logsContent.setAttribute('data-i18n', 'failed_to_load_logs');
        logsContent.textContent = i18next.t('failed_to_load_logs');
        console.error('Fetch notification logs request failed:', error);
      }
    });

    card.appendChild(logsCollapse);
    this.notificationListTbody.appendChild(card);
  }

  showNotificationInfo(notification) {
    this.currentNotification = notification;
    this.infoModalBody.innerHTML = '';

    const pasteInstruction = document.createElement('p');
    pasteInstruction.textContent = i18next.t('paste_token_instruction');
    this.infoModalBody.appendChild(pasteInstruction);

    const tokenContainer = document.createElement('div');
    tokenContainer.classList.add('d-flex', 'align-items-center', 'mb-3');

    const tokenSpan = document.createElement('span');
    tokenSpan.textContent = notification.token;
    tokenSpan.classList.add('me-2', 'text-break');
    tokenContainer.appendChild(tokenSpan);

    const copyIcon = document.createElement('div');
    copyIcon.style.cursor = 'pointer';
    copyIcon.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-copy" viewBox="0 0 16 16">
        <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
      </svg>
    `;
    copyIcon.addEventListener('click', () => {
      this.copyNotificationValue(notification.token);
    });
    tokenContainer.appendChild(copyIcon);
    this.infoModalBody.appendChild(tokenContainer);

    const qrInstruction = document.createElement('p');
    qrInstruction.textContent = i18next.t('scan_qr_code_instruction');
    this.infoModalBody.appendChild(qrInstruction);

    const subscribeUrl = `${window.location.origin}/?token=${encodeURIComponent(
      notification.token
    )}`;

    const subscribeUrlContainer = document.createElement('div');
    subscribeUrlContainer.classList.add('d-flex', 'align-items-center', 'mb-3');

    const subscribeUrlSpan = document.createElement('span');
    subscribeUrlSpan.textContent = subscribeUrl;
    subscribeUrlSpan.classList.add('me-2', 'text-break');
    subscribeUrlContainer.appendChild(subscribeUrlSpan);

    const copySubscribeUrlIcon = copyIcon.cloneNode(true);
    copySubscribeUrlIcon.addEventListener('click', () => {
      this.copyNotificationValue(subscribeUrl);
    });
    subscribeUrlContainer.appendChild(copySubscribeUrlIcon);
    this.infoModalBody.appendChild(subscribeUrlContainer);

    const qrContainer = document.createElement('div');
    qrContainer.classList.add('text-center', 'mt-3');
    this.infoModalBody.appendChild(qrContainer);

    new QRCode(qrContainer, {
      text: subscribeUrl,
      width: 200,
      height: 200,
    });

    bootstrap.Modal.getOrCreateInstance(this.infoModal).show();
  }

  async copyNotificationValue(value) {
    try {
      await navigator.clipboard.writeText(value);
      this.addToast(
        'success',
        i18next.t('copy_success_title'),
        i18next.t('just_now'),
        i18next.t('copy_success_body')
      );
    } catch (error) {
      console.error('Failed to copy notification value:', error);
    }
  }

  async deleteNotification(notificationId) {
    this.showConfirmationModal(
      i18next.t('delete_notification_confirmation'),
      async () => {
        try {
          const res = await this.apiClient.deleteNotification(notificationId);
          if (res.ok) {
            this.updateNotifications();
          } else {
            console.error('Failed to delete notification:', res.statusText);
          }
        } catch (error) {
          console.error('Delete notification request failed:', error);
        }
      }
    );
  }

  async updateSubscriptions() {
    try {
      const res = await this.apiClient.getSubscriptions();
      if (res.ok) {
        const subscriptions = await res.json();
        this.clearElementHTML(this.subscriptionListTbody);
        subscriptions.forEach((subscription) => {
          this.createSubscriptionRow(subscription);
        });
      } else {
        console.error('Failed to fetch subscriptions:', res.statusText);
      }
    } catch (error) {
      console.error('Fetch subscriptions request failed:', error);
    }
  }

  createSubscriptionRow(subscription) {
    const card = document.createElement('div');
    card.classList.add('subscription-card');

    const cardBody = document.createElement('div');
    cardBody.classList.add('subscription-card__body');

    const headerRow = document.createElement('div');
    headerRow.classList.add('subscription-card__header');

    const nameText = document.createElement('div');
    nameText.classList.add('subscription-card__title');
    nameText.textContent = subscription.notification_name;

    const statusBadge = document.createElement('span');
    statusBadge.classList.add(
      'subscription-card__badge',
      subscription.is_muted ? 'is-disabled' : 'is-enabled'
    );
    statusBadge.setAttribute(
      'data-i18n',
      subscription.is_muted ? 'disabled' : 'enabled'
    );
    statusBadge.textContent = subscription.is_muted
      ? i18next.t('disabled')
      : i18next.t('enabled');

    headerRow.appendChild(nameText);
    headerRow.appendChild(statusBadge);

    const infoList = document.createElement('div');
    infoList.classList.add('subscription-card__info');

    const tokenRow = document.createElement('div');
    tokenRow.classList.add('subscription-card__row');

    const tokenLabel = document.createElement('span');
    tokenLabel.classList.add('subscription-card__label');
    tokenLabel.setAttribute('data-i18n', 'token');
    tokenLabel.textContent = i18next.t('token');

    const tokenValue = document.createElement('span');
    tokenValue.classList.add('subscription-card__value');
    tokenValue.textContent = subscription.notification_token;
    tokenValue.style.cursor = 'pointer';
    tokenValue.addEventListener('click', () => {
      navigator.clipboard.writeText(subscription.notification_token);
    });

    tokenRow.appendChild(tokenLabel);
    tokenRow.appendChild(tokenValue);

    const subscribedRow = document.createElement('div');
    subscribedRow.classList.add('subscription-card__row');

    const subscribedLabel = document.createElement('span');
    subscribedLabel.classList.add('subscription-card__label');
    subscribedLabel.setAttribute('data-i18n', 'subscribe_time');
    subscribedLabel.textContent = i18next.t('subscribe_time');

    const subscribedValue = document.createElement('span');
    subscribedValue.classList.add('subscription-card__value');
    subscribedValue.textContent = new Date(
      subscription.subscribed_at
    ).toLocaleString();

    subscribedRow.appendChild(subscribedLabel);
    subscribedRow.appendChild(subscribedValue);

    infoList.appendChild(tokenRow);
    infoList.appendChild(subscribedRow);

    const actionsRow = document.createElement('div');
    actionsRow.classList.add('subscription-card__actions');

    const enableButton = document.createElement('button');
    enableButton.classList.add('btn', 'btn-secondary', 'btn-sm');
    enableButton.setAttribute(
      'data-i18n',
      subscription.is_muted ? 'enable' : 'disable'
    );
    enableButton.textContent = subscription.is_muted
      ? i18next.t('enable')
      : i18next.t('disable');
    enableButton.addEventListener('click', () =>
      this.updateSubscription(subscription)
    );

    const unsubscribeButton = document.createElement('button');
    unsubscribeButton.classList.add('btn', 'btn-danger', 'btn-sm');
    unsubscribeButton.setAttribute('data-i18n', 'unsubscribe');
    unsubscribeButton.textContent = i18next.t('unsubscribe');
    unsubscribeButton.addEventListener('click', () =>
      this.deleteSubscription(subscription.notification_id)
    );

    actionsRow.appendChild(enableButton);
    actionsRow.appendChild(unsubscribeButton);

    cardBody.appendChild(headerRow);
    if (subscription.notification_description) {
      const descriptionText = document.createElement('div');
      descriptionText.classList.add('subscription-card__desc');
      descriptionText.textContent = subscription.notification_description;
      cardBody.appendChild(descriptionText);
    }
    cardBody.appendChild(infoList);
    cardBody.appendChild(actionsRow);
    card.appendChild(cardBody);

    this.subscriptionListTbody.appendChild(card);
  }

  async updateSubscription(subscription) {
    const isMuted = !subscription.is_muted;
    try {
      const res = await this.apiClient.updateSubscription(
        subscription.notification_id,
        {
          is_muted: isMuted,
        }
      );
      if (res.ok) {
        this.updateSubscriptions();
      } else {
        console.error('Failed to update subscription:', res.statusText);
      }
    } catch (error) {
      console.error('Update subscription request failed:', error);
    }
  }

  async deleteSubscription(notificationId) {
    this.showConfirmationModal(
      i18next.t('unsubscribe_notification_confirmation'),
      async () => {
        try {
          const res = await this.apiClient.deleteSubscription(notificationId);
          if (res.ok) {
            this.updateSubscriptions();
          } else {
            console.error('Failed to delete subscription:', res.statusText);
          }
        } catch (error) {
          console.error('Delete subscription request failed:', error);
        }
      }
    );
  }

  // Show/Hide sections based on login state
  showLoginState() {
    this.currentUser = null;
    // Navigation
    this.userNavItem.textContent = 'Unknown';
    this.show(this.notLoggedInMenu);
    this.hide(this.loggedInMenu);

    // Notification
    this.show(this.notificationLoginButton, 'inline-block');
    this.hide(this.notificationPanel);
  }

  showLoggedInState(username) {
    this.currentUser = username;
    // Navigation
    this.userNavItem.textContent = username;
    this.hide(this.notLoggedInMenu);
    this.show(this.loggedInMenu);

    // Notification
    this.hide(this.notificationLoginButton);
    this.show(this.notificationPanel);
  }

  showError(error) {
    this.errorMessage.textContent = error.message;
    // this.errorStack.textContent = error.stack;
    this.showPage(this.pages['error-page']);
  }

  getToastStatusConfig(status) {
    const normalizedStatus = String(status || 'info').toLowerCase();
    switch (normalizedStatus) {
      case 'success':
        return {
          iconClass: 'bi-check-circle-fill',
          textClass: 'text-success',
        };
      case 'warning':
        return {
          iconClass: 'bi-exclamation-triangle-fill',
          textClass: 'text-warning',
        };
      case 'error':
      case 'danger':
      case 'failed':
        return {
          iconClass: 'bi-x-circle-fill',
          textClass: 'text-danger',
        };
      default:
        return {
          iconClass: 'bi-info-circle-fill',
          textClass: 'text-primary',
        };
    }
  }

  addToast(status, title, timeText, body) {
    if (!this.toastContainer) {
      console.warn('Toast container not found');
      return;
    }

    const autoHideDelayMs = 4000;

    const { iconClass, textClass } = this.getToastStatusConfig(status);

    const toastElement = document.createElement('div');
    toastElement.classList.add('toast');
    toastElement.setAttribute('role', 'alert');
    toastElement.setAttribute('aria-live', 'assertive');
    toastElement.setAttribute('aria-atomic', 'true');

    const toastHeader = document.createElement('div');
    toastHeader.classList.add('toast-header');

    const statusIcon = document.createElement('i');
    statusIcon.classList.add('bi', iconClass, 'me-2', textClass);
    statusIcon.setAttribute('aria-hidden', 'true');

    const titleElement = document.createElement('strong');
    titleElement.classList.add('me-auto');
    titleElement.textContent = title || '';

    const timeElement = document.createElement('small');
    timeElement.classList.add('text-body-secondary');
    timeElement.textContent = timeText || '';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.classList.add('btn-close');
    closeButton.setAttribute('data-bs-dismiss', 'toast');
    closeButton.setAttribute('aria-label', 'Close');

    toastHeader.appendChild(statusIcon);
    toastHeader.appendChild(titleElement);
    toastHeader.appendChild(timeElement);
    toastHeader.appendChild(closeButton);

    const toastBody = document.createElement('div');
    toastBody.classList.add('toast-body');
    toastBody.textContent = body || '';

    const progress = document.createElement('div');
    progress.classList.add('progress');
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-label', 'Toast timeout');
    progress.setAttribute('aria-valuenow', '100');
    progress.setAttribute('aria-valuemin', '0');
    progress.setAttribute('aria-valuemax', '100');
    progress.style.height = '4px';

    const progressBar = document.createElement('div');
    progressBar.classList.add('progress-bar');
    progressBar.style.width = '100%';
    progressBar.style.transition = 'none';
    progress.appendChild(progressBar);

    toastElement.appendChild(toastHeader);
    toastElement.appendChild(toastBody);
    toastElement.appendChild(progress);

    this.toastContainer.appendChild(toastElement);

    const toastInstance = bootstrap.Toast.getOrCreateInstance(toastElement, {
      autohide: false,
    });

    let animationFrameId = null;
    let startAt = 0;

    const animateProgress = (now) => {
      const elapsed = now - startAt;
      const percent = Math.max(100 - (elapsed / autoHideDelayMs) * 100, 0);
      progressBar.style.width = `${percent}%`;
      progress.setAttribute('aria-valuenow', String(Math.round(percent)));

      if (percent > 0) {
        animationFrameId = window.requestAnimationFrame(animateProgress);
      } else {
        toastInstance.hide();
      }
    };

    toastElement.addEventListener(
      'shown.bs.toast',
      () => {
        startAt = performance.now();
        animationFrameId = window.requestAnimationFrame(animateProgress);
      },
      { once: true }
    );

    toastElement.addEventListener(
      'hidden.bs.toast',
      () => {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }
      },
      { once: true }
    );

    toastElement.addEventListener('mouseenter', () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    });

    toastElement.addEventListener('mouseleave', () => {
      startAt =
        performance.now() -
        (autoHideDelayMs * (100 - parseFloat(progressBar.style.width))) / 100;
      animationFrameId = window.requestAnimationFrame(animateProgress);
    });

    toastInstance.show();
  }

  enableBootstrapComponents() {
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]'
    );
    tooltipTriggerList.forEach(
      (tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl)
    );
  }

  updateLocalization() {
    document.querySelectorAll('[data-i18n]').forEach((element) => {
      const key = element.getAttribute('data-i18n');
      element.textContent = i18next.t(key);
    });

    document.querySelectorAll('[data-i18n-placeholder]').forEach((element) => {
      const key = element.getAttribute('data-i18n-placeholder');
      element.setAttribute('placeholder', i18next.t(key));
    });

    document.querySelectorAll('[data-i18n-aria-label]').forEach((element) => {
      const key = element.getAttribute('data-i18n-aria-label');
      element.setAttribute('aria-label', i18next.t(key));
    });

    document.querySelectorAll('[data-i18n-alt]').forEach((element) => {
      const key = element.getAttribute('data-i18n-alt');
      element.setAttribute('alt', i18next.t(key));
    });
  }

  changeLanguage(lang) {
    localStorage.setItem('lang', lang);
    i18next.changeLanguage(lang, this.updateLocalization);
  }

  initI18next() {
    const browserLanguage = navigator.language || 'en';
    const lang =
      localStorage.getItem('lang') ||
      (browserLanguage.toLowerCase().startsWith('zh') ? 'zh-TW' : 'en');
    i18next.init(
      {
        lng: lang,
        debug: true,
        resources: {
          en: { translation: enTranslation },
          'zh-TW': { translation: zhTWTranslation },
        },
      },
      () => {
        this.updateLocalization();
      }
    );
  }

  handleQrCodeSubscription() {
    const params = new URLSearchParams(window.location.search);
    const qrToken = params.get('token');
    if (!qrToken) {
      return;
    }

    this.showPage(this.pages['subscription-page']);
    this.newSubscriptionTokenInput.value = qrToken;
    this.newSubscriptionButton.click();
    window.history.replaceState(null, '', window.location.pathname);
  }

  updateMobileUuid(uuid) {
    this.mobileUuidSpan.textContent = uuid;
  }
}
